import * as React from 'react';
import { Search, X } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { cn } from '@/lib/utils';

export interface SearchInputProps extends Omit<React.InputHTMLAttributes<HTMLInputElement>, 'onChange' | 'value'> {
  value: string;
  /** Called with the new value on change, and with '' when the clear button is pressed. */
  onValueChange: (value: string) => void;
  /** Render a leading magnifier icon. */
  withIcon?: boolean;
}

/**
 * Text input with an inline clear (X) button that appears once there is a value.
 * Used for all keyword/search fields so they can be emptied with one click.
 */
const SearchInput = React.forwardRef<HTMLInputElement, SearchInputProps>(
  ({ value, onValueChange, withIcon = false, className, ...props }, ref) => {
    return (
      <div className="relative">
        {withIcon && (
          <Search className="pointer-events-none absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
        )}
        <Input
          ref={ref}
          value={value}
          onChange={(e) => onValueChange(e.target.value)}
          className={cn(withIcon && 'pl-8', value && 'pr-8', className)}
          {...props}
        />
        {value && (
          <button
            type="button"
            onClick={() => onValueChange('')}
            aria-label="Clear search"
            className="absolute right-2 top-1/2 -translate-y-1/2 rounded text-muted-foreground hover:text-foreground"
          >
            <X className="h-3.5 w-3.5" />
          </button>
        )}
      </div>
    );
  },
);
SearchInput.displayName = 'SearchInput';

export { SearchInput };
